import db from "./offlineDb";
import { getOfflineContext, getOfflineNamespace, getOfflineTermId, setOfflineTermSession, updateOfflineContextFromAuth } from "./offlineContext";
import { applyOfflineMutationToCache } from "./offlineMutations";

function cloneForOffline(data) {
  if (Array.isArray(data)) return data.map((item) => ({ ...item }));
  if (data && typeof data === "object") return { ...data };
  return data;
}

function annotateCachedData(data, meta) {
  if (data == null) return data;
  const cloned = cloneForOffline(data);
  if (cloned && typeof cloned === "object") {
    Object.defineProperty(cloned, "_offlineMeta", {
      value: meta,
      enumerable: false,
      configurable: true,
    });
  }
  return cloned;
}

function readJsonStorage(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function writeJsonStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function buildOfflineMeta(row, extra = {}) {
  return {
    isAvailableOffline: true,
    isStale: !!row?.stale,
    lastFetchedAt: row?.lastFetchedAt ?? row?.cachedAt ?? null,
    preloadInFlight: row?.status === "syncing",
    ...extra,
  };
}

function normalizeUrl(rawUrl) {
  return rawUrl.replace(/^\//, "");
}

function getMethod(args, api) {
  return (typeof args === "object" ? args?.method : null) ?? (api.type === "query" ? "GET" : "POST");
}

function getBody(args) {
  return typeof args === "object" ? args?.body ?? {} : {};
}

function getParams(url) {
  return new URLSearchParams(url.split("?")[1] || "");
}

async function getPreloadMeta(state, classroomId, termId) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId || !termId) return null;
  return db.preloadStatus.get([ctx.schoolId, ctx.userId, termId, classroomId]);
}

async function readStudents(url, state) {
  const classroomId = parseInt(url.match(/classroom\/(\d+)\/students/)?.[1]);
  const termId = getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId || !termId) return null;
  const row = await db.students.get([ctx.schoolId, ctx.userId, termId, classroomId]);
  if (!row?.data) return null;
  const preload = await getPreloadMeta(state, classroomId, termId);
  return annotateCachedData(row.data, buildOfflineMeta(preload || row));
}

async function writeStudents(url, data, state) {
  const classroomId = parseInt(url.match(/classroom\/(\d+)\/students/)?.[1]);
  const ctx = getOfflineNamespace(state);
  if (!ctx.schoolId || !ctx.userId || !ctx.termId || !classroomId || !Array.isArray(data)) return;
  await db.students.put({
    ...ctx,
    classroomId,
    data,
    cachedAt: Date.now(),
  });
}

async function readRecords(url, state) {
  const params = getParams(url);
  const subjectId = parseInt(params.get("subject_id"));
  const classroomId = parseInt(params.get("classroom_id"));
  const termId = parseInt(params.get("term_id")) || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !subjectId || !classroomId || !termId) return null;
  const row = await db.records.get([ctx.schoolId, ctx.userId, termId, classroomId, subjectId]);
  if (!row?.data) return null;
  const preload = await getPreloadMeta(state, classroomId, termId);
  return annotateCachedData(row.data, buildOfflineMeta(preload || row));
}

async function writeRecords(url, data, state) {
  const params = getParams(url);
  const subjectId = parseInt(params.get("subject_id"));
  const classroomId = parseInt(params.get("classroom_id"));
  const termId = parseInt(params.get("term_id")) || data?.[0]?.term_id || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !subjectId || !classroomId || !termId || !Array.isArray(data)) return;
  await db.records.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? null,
    termId,
    classroomId,
    subjectId,
    data,
    cachedAt: Date.now(),
  });
}

async function readSubjectComments(url, state) {
  const path = url.split("?")[0].split("/");
  const subjectId = parseInt(path[2]);
  const classroomId = parseInt(path[3]);
  const type = getParams(url).get("type") || "term";
  const termId = parseInt(getParams(url).get("term_id")) || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !subjectId || !classroomId || !termId) return null;
  const row = await db.comments.get([ctx.schoolId, ctx.userId, termId, 0, classroomId, 0, 0, `subject:${subjectId}:${type}`]);
  if (!row?.data) return null;
  const preload = await getPreloadMeta(state, classroomId, termId);
  return annotateCachedData(row.data, buildOfflineMeta(preload || row));
}

async function writeSubjectComments(url, data, state) {
  const path = url.split("?")[0].split("/");
  const subjectId = parseInt(path[2]);
  const classroomId = parseInt(path[3]);
  const type = getParams(url).get("type") || "term";
  const termId = parseInt(getParams(url).get("term_id")) || data?.[0]?.term_id || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !subjectId || !classroomId || !termId || !Array.isArray(data)) return;
  await db.comments.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? null,
    termId,
    studentId: 0,
    classroomId,
    subjectId,
    sectionId: 0,
    departmentId: 0,
    type: `subject:${subjectId}:${type}`,
    data,
    cachedAt: Date.now(),
  });
}

async function readStudentComments(url, state) {
  const studentId = parseInt(url.split("?")[0].split("/")[2]);
  const type = getParams(url).get("type") || "term";
  const termId = parseInt(getParams(url).get("term_id")) || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !studentId || !termId) return null;

  const rows = await db.comments
    .where("[schoolId+userId+termId+studentId+classroomId+sectionId+departmentId+type]")
    .between(
      [ctx.schoolId, ctx.userId, termId, studentId, Dexie.minKey, Dexie.minKey, Dexie.minKey, Dexie.minKey],
      [ctx.schoolId, ctx.userId, termId, studentId, Dexie.maxKey, Dexie.maxKey, Dexie.maxKey, Dexie.maxKey]
    )
    .toArray();
  const filtered = rows
    .filter((row) => {
      const rowType = String(row.type || "");
      return rowType === type || rowType.endsWith(`:${type}`);
    })
    .flatMap((row) => row.data || []);
  return filtered.length > 0 ? annotateCachedData(filtered, buildOfflineMeta(rows[0])) : null;
}

async function writeCurrentTerm(_url, data) {
  if (data?.id) {
    setOfflineTermSession({ termId: data.id, sessionId: data.session_id ?? null });
    writeJsonStorage("schoola_current_term", data);
  }
}

async function readCurrentTerm() {
  const stored = readJsonStorage("schoola_current_term");
  if (stored?.id) return annotateCachedData(stored, buildOfflineMeta({ cachedAt: Date.now() }));
  const ctx = getOfflineContext();
  return ctx.termId ? annotateCachedData({ id: ctx.termId, session_id: ctx.sessionId ?? null }, buildOfflineMeta({ cachedAt: Date.now() })) : null;
}

async function readCurrentSession() {
  const session = readJsonStorage("schoola_current_session");
  return session ? annotateCachedData(session, buildOfflineMeta({ cachedAt: Date.now() })) : null;
}

async function writeCurrentSession(_url, data) {
  if (data?.id) {
    writeJsonStorage("schoola_current_session", data);
  }
}

async function readSchoolProfile() {
  const profile = readJsonStorage("schoola_school_profile");
  return profile ? annotateCachedData(profile, buildOfflineMeta({ cachedAt: Date.now() })) : null;
}

async function writeSchoolProfile(_url, data) {
  if (data?.id) {
    writeJsonStorage("schoola_school_profile", data);
  }
}

async function readClassroomById(url) {
  const classroomId = parseInt(url.split("/")[1]);
  if (!classroomId) return null;
  const sources = await Promise.all([db.allClasses.get(1), db.myClasses.get(1)]);
  for (const row of sources) {
    const found = row?.data?.find((item) => Number(item.id) === classroomId);
    if (found) {
      return annotateCachedData(found, buildOfflineMeta(row));
    }
  }
  return null;
}

async function readClassesBySection(url) {
  const sectionId = parseInt(url.split("/")[3]);
  if (!sectionId) return null;
  const row = await db.allClasses.get(1);
  if (!row?.data) return null;
  const filtered = row.data.filter((item) => Number(item.section_id) === sectionId);
  return filtered.length > 0 ? annotateCachedData(filtered, buildOfflineMeta(row)) : null;
}

async function readTraitGrades(url, state) {
  const parts = url.split("/");
  const studentId = parseInt(parts[3]);
  const classroomId = parseInt(parts[4]);
  const termId = parseInt(parts[5]) || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !studentId || !classroomId || !termId) return null;
  const row = await db.traitGrades.get([ctx.schoolId, ctx.userId, termId, classroomId, studentId]);
  return row?.data ? annotateCachedData(row.data, buildOfflineMeta(row)) : null;
}

async function readAssessments(state) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !ctx.termId) return null;
  const rows = await db.assessments
    .where("[schoolId+userId+termId+classroomId]")
    .between([ctx.schoolId, ctx.userId, ctx.termId, Dexie.minKey], [ctx.schoolId, ctx.userId, ctx.termId, Dexie.maxKey])
    .toArray();
  if (!rows.length) return null;
  const seen = new Set();
  const flat = [];
  for (const row of rows) {
    for (const item of row.data || []) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        flat.push(item);
      }
    }
  }
  return annotateCachedData(flat, buildOfflineMeta(rows[0]));
}

async function writeAssessments(_url, data, state) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !ctx.termId || !Array.isArray(data)) return;
  await db.assessments.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? null,
    termId: ctx.termId,
    classroomId: 0,
    data,
    cachedAt: Date.now(),
  });
}

async function writeTraitGrades(url, data, state) {
  const parts = url.split("/");
  const studentId = parseInt(parts[3]);
  const classroomId = parseInt(parts[4]);
  const termId = parseInt(parts[5]) || data?.[0]?.term_id || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !studentId || !classroomId || !termId || !Array.isArray(data)) return;
  await db.traitGrades.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? null,
    termId,
    classroomId,
    studentId,
    data,
    cachedAt: Date.now(),
  });
}

async function readSimple(tableName, id = 1) {
  const row = await db[tableName].get(id);
  return row?.data ? annotateCachedData(row.data, buildOfflineMeta(row)) : null;
}

async function writeSimple(tableName, data, id = 1) {
  if (Array.isArray(data)) {
    await db[tableName].put({ id, data, cachedAt: Date.now() });
  }
}

async function readAttendance(url, state) {
  const classroomId = parseInt(url.split("/")[1]);
  const termId = getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId || !termId) return null;
  const row = await db.attendance.get([ctx.schoolId, ctx.userId, termId, classroomId]);
  return row?.data ? annotateCachedData(row.data, buildOfflineMeta(row)) : null;
}

async function writeAttendance(url, data, state) {
  const classroomId = parseInt(url.split("/")[1]);
  const termId = data?.[0]?.term_id || getOfflineTermId(state);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId || !termId || !Array.isArray(data)) return;
  await db.attendance.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? null,
    termId,
    classroomId,
    data,
    cachedAt: Date.now(),
  });
}

async function readTraits(state) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId) return null;
  const rows = await db.traits
    .where("[schoolId+userId+subsectionId]")
    .between([ctx.schoolId, ctx.userId, Dexie.minKey], [ctx.schoolId, ctx.userId, Dexie.maxKey])
    .toArray();
  const flat = rows.flatMap((row) => row.data || []);
  return flat.length > 0 ? annotateCachedData(flat, buildOfflineMeta(rows[0])) : null;
}

async function writeTraits(_url, data, state) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !Array.isArray(data)) return;
  const grouped = data.reduce((acc, item) => {
    const subsectionId = item.subsection_id ?? 0;
    acc[subsectionId] = acc[subsectionId] || [];
    acc[subsectionId].push(item);
    return acc;
  }, {});
  for (const [subsectionId, rows] of Object.entries(grouped)) {
    await db.traits.put({
      schoolId: ctx.schoolId,
      userId: ctx.userId,
      subsectionId: parseInt(subsectionId),
      data: rows,
      cachedAt: Date.now(),
    });
  }
}

async function readClassroomTraits(url, state) {
  const parts = url.split("/");
  const classroomId = parseInt(parts[parts.length - 1]);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId) return null;
  const row = await db.classroomTraits.get([ctx.schoolId, ctx.userId, classroomId]);
  return row?.data ? annotateCachedData(row.data, buildOfflineMeta(row)) : null;
}

async function writeClassroomTraits(url, data, state) {
  const parts = url.split("/");
  const classroomId = parseInt(parts[parts.length - 1]);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !classroomId || !Array.isArray(data)) return;
  await db.classroomTraits.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    classroomId,
    data,
    cachedAt: Date.now(),
  });
}

async function readGrades(url, state) {
  const parts = url.split("/");
  const type = parts[2];
  const subsectionId = parseInt(parts[3]);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !type || !subsectionId) return null;
  const row = await db.grades.get([ctx.schoolId, ctx.userId, subsectionId, type]);
  return row?.data ? annotateCachedData(row.data, buildOfflineMeta(row)) : null;
}

async function writeGrades(url, data, state) {
  const parts = url.split("/");
  const type = parts[2];
  const subsectionId = parseInt(parts[3]);
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !type || !subsectionId || !Array.isArray(data)) return;
  await db.grades.put({
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    subsectionId,
    type,
    data,
    cachedAt: Date.now(),
  });
}

const Dexie = db.constructor;

const CACHE_READERS = [
  { test: (url) => /^classroom\/\d+\/students/.test(url), read: readStudents, write: writeStudents },
  { test: (url) => url.startsWith("record/subject"), read: readRecords, write: writeRecords },
  { test: (url) => /^comment\/subject\/\d+\/\d+/.test(url), read: readSubjectComments, write: writeSubjectComments },
  { test: (url) => /^comment\/student\/\d+/.test(url), read: readStudentComments },
  { test: (url) => /^trait\/grade\/student\/\d+\/\d+/.test(url), read: readTraitGrades, write: writeTraitGrades },
  { test: (url) => url === "assessment/all", read: (_url, state) => readAssessments(state), write: writeAssessments },
  { test: (url) => url === "classroom/all/mine", read: () => readSimple("myClasses"), write: (_url, data) => writeSimple("myClasses", data) },
  { test: (url) => url === "classroom/all", read: () => readSimple("allClasses"), write: (_url, data) => writeSimple("allClasses", data) },
  { test: (url) => /^classroom\/all\/section\/\d+$/.test(url), read: readClassesBySection },
  { test: (url) => /^classroom\/\d+$/.test(url), read: readClassroomById },
  { test: (url) => url === "teacher/subjects", read: () => readSimple("subjects"), write: (_url, data) => writeSimple("subjects", data) },
  { test: (url) => url === "section/all", read: () => readSimple("sections"), write: (_url, data) => writeSimple("sections", data) },
  { test: (url) => url === "department/all", read: () => readSimple("departments"), write: (_url, data) => writeSimple("departments", data) },
  { test: (url) => url.startsWith("attendance/"), read: readAttendance, write: writeAttendance },
  { test: (url) => url === "trait/all", read: (_url, state) => readTraits(state), write: writeTraits },
  { test: (url) => /^trait\/for-classroom\/\d+$/.test(url), read: readClassroomTraits, write: writeClassroomTraits },
  { test: (url) => /^grade\/all\/[^/]+\/\d+$/.test(url), read: readGrades, write: writeGrades },
  { test: (url) => url === "session/current", read: readCurrentSession, write: writeCurrentSession },
  { test: (url) => url === "session/term/current", read: readCurrentTerm, write: writeCurrentTerm },
  { test: (url) => url === "school/profile", read: readSchoolProfile, write: writeSchoolProfile },
];

function findCacheEntry(url) {
  return CACHE_READERS.find((entry) => entry.test(url));
}

function classifyMutation(args) {
  const rawUrl = typeof args === "string" ? args : args?.url ?? "";
  const url = normalizeUrl(rawUrl);
  const method = (typeof args === "object" ? args?.method : null) ?? "POST";

  if (url.startsWith("record/create") || (url.startsWith("record") && method === "POST")) {
    return { entityType: "record", action: "create" };
  }
  if (url.startsWith("record") && method === "DELETE") {
    return { entityType: "record", action: "delete" };
  }
  if (url.startsWith("comment/create") || (url.startsWith("comment") && method === "POST" && !url.includes("ai"))) {
    return { entityType: "comment", action: "create" };
  }
  if (url.startsWith("comment") && method === "DELETE") {
    return { entityType: "comment", action: "delete" };
  }
  if (url.startsWith("trait/grade/create") || (url.startsWith("trait/grade") && method === "POST")) {
    return { entityType: "trait_grade", action: "create" };
  }
  if (url.startsWith("trait/grade") && method === "DELETE") {
    return { entityType: "trait_grade", action: "delete" };
  }
  if (url.startsWith("attendance/create") || (url.startsWith("attendance") && method === "POST")) {
    return { entityType: "attendance", action: "create" };
  }
  if (url.startsWith("attendance/clear") || (url.startsWith("attendance") && method === "DELETE")) {
    return { entityType: "attendance", action: "clear" };
  }
  return { entityType: "unknown", action: method.toLowerCase() };
}

export function createOfflineBaseQuery(onlineBaseQuery) {
  return async function offlineBaseQuery(args, api, extraOptions) {
    updateOfflineContextFromAuth(api.getState?.());

    const isOnline = navigator.onLine;
    const rawUrl = typeof args === "string" ? args : args?.url ?? "";
    const url = normalizeUrl(rawUrl);
    const method = getMethod(args, api);
    const isQuery = method === "GET" && api.type === "query";
    const isMutation = !isQuery;

    if (!isOnline && isQuery) {
      const cacheEntry = findCacheEntry(url);
      if (cacheEntry) {
        const cached = await cacheEntry.read(url, api.getState?.()).catch(() => null);
        if (cached !== null) {
          return { data: cached };
        }
      }
      return {
        error: {
          status: "OFFLINE_NO_CACHE",
          error: "You are offline and no cached data is available for this request.",
        },
      };
    }

    if (!isOnline && isMutation) {
      const body = getBody(args);
      const { entityType, action } = classifyMutation(args);
      const ctx = getOfflineContext(api.getState?.());
      const payload = {
        ...body,
        term_id: body?.term_id ?? ctx.termId ?? null,
      };

      await db.syncQueue.add({
        entityType,
        action,
        payload,
        schoolId: ctx.schoolId ?? null,
        userId: ctx.userId ?? null,
        sessionId: ctx.sessionId ?? null,
        termId: payload.term_id ?? null,
        classroomId: payload.classroom_id ?? null,
        subjectId: payload.subject_id ?? null,
        studentId: payload.student_id ?? null,
        quizId: payload.quiz_id ?? null,
        status: "pending",
        client_timestamp: new Date().toISOString(),
        createdAt: Date.now(),
        retries: 0,
      });

      await applyOfflineMutationToCache(
        typeof args === "object" ? { ...args, body: payload } : args,
        api.getState?.()
      ).catch(() => {});

      return { data: { queued: true, offline: true } };
    }

    const result = await onlineBaseQuery(args, api, extraOptions);

    if (!result.error && isQuery && result.data !== undefined) {
      const cacheEntry = findCacheEntry(url);
      if (cacheEntry?.write) {
        await cacheEntry.write(url, result.data, api.getState?.()).catch(() => {});
      }
    }

    return result;
  };
}
